import { HttpClient, HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";

import { ModalOptions } from "@ionic/core";
import { ModalController } from "@ionic/angular";
import { Action, State, StateContext } from "@ngxs/store";
import { from, Subject, of, Observable, throwError, EMPTY } from "rxjs";
import { filter, map, switchMap, tap, catchError } from "rxjs/operators";

import { NotLinkedToAccountWindowComponent } from "../../core/components/not-linked-to-account-window/not-linked-to-account-window.component";
import { AccountInfo, MapData, User, Vehicle } from "../../core/models";
import { StorageService } from "../../core/services/storage.service";
import { ToastService } from "../../core/services/toast.service";
import { SetActiveVehiclesAction } from "../active-vehicle/active-vehicle.actions";
import { GetLastActiveFeatureAction } from "../feature/feature.actions";
import { parseJwt } from "../utils";

import {
  GetCurrentUserAction,
  GetCurrentUserSuccessAction,
  GetCurrentUserFailAction,
  LoadCurrentUserAction,
  LoadCurrentUserSuccessAction,
  LoadCurrentUserFailAction,
  SetCurrentUserAction,
  LoadMapDataAction,
  LoadMapDataSuccessAction,
  SaveMapDataAction,
  SaveMapDataSuccessAction,
  SetAccessTokenAction,
  ClearAccessTokenAction,
  CheckAccessTokenAction,
  LogoutAction,
  GetAccountInfoAction,
  GetAccountInfoSuccessAction,
  GetAccountInfoFailAction,
  SetAccountInfoAction,
  SaveAccountInfoAction,
  SaveAccountInfoSuccessAction,
  SaveAccountInfoFailAction,
  SubscribeToPremiumPlanAction,
  SubscribeToPremiumPlanSuccessAction,
  SubscribeToPremiumPlanFailAction,
  RenewUserVehiclesSubscriptionAction,
  RenewUserVehiclesSubscriptionSuccessAction,
  RenewUserVehiclesSubscriptionFailAction,
  SetCurrentUserPendingVehiclesAction,
} from "./user.actions";
import { dispatchRequest } from "../dispatch-request";
import {
  defaultRequestState,
  IRequestsNestedState,
} from "../interfaces/requests.interface";

export interface UserStateModel {
  entities: { [key: number]: User } | null;
  ids: number[];
  currentUser: User | null;
  currentUserId: number | null;
  currentUserPendingVehicles: Vehicle[];
  mapData: MapData | null;
  accessToken: string | null;
  accountInfo: AccountInfo | null;
  loading: boolean;
  error: any;
  currentUserRequestState: IRequestsNestedState;
  getAccountInfoRequestState: IRequestsNestedState;
  mapDataRequestState: IRequestsNestedState;
  subscribeToPremiumPlanRequestState: IRequestsNestedState;
  renewUserVehiclesSubscriptionPutRequestState: IRequestsNestedState;
}

@State<UserStateModel>({
  name: "user",
  defaults: {
    entities: {},
    ids: [],
    currentUser: null,
    currentUserId: null,
    currentUserPendingVehicles: [],
    mapData: null,
    accessToken: null,
    accountInfo: null,
    loading: false,
    error: null,
    currentUserRequestState: { ...defaultRequestState },
    getAccountInfoRequestState: { ...defaultRequestState },
    mapDataRequestState: { ...defaultRequestState },
    subscribeToPremiumPlanRequestState: { ...defaultRequestState },
    renewUserVehiclesSubscriptionPutRequestState: { ...defaultRequestState },
  },
})
@Injectable()
export class UserState {
  openNoLinkedModalEvent$: Subject<void> = new Subject();

  constructor(
    private httpClient: HttpClient,
    private storageService: StorageService,
    private modalController: ModalController,
    private toastService: ToastService
  ) {
    this.openNoLinkedModalEvent$
      .pipe(
        switchMap(() => {
          const modalComponent: ModalOptions = {
            component: NotLinkedToAccountWindowComponent,
            cssClass: "not-linked-to-account-window-modal",
            animated: false,
            backdropDismiss: false,
          };
          return from(this.modalController.create(modalComponent));
        })
      )
      .subscribe((modal) => {
        modal.present();
      });
  }

  @Action(LoadCurrentUserAction)
  loadCurrentUser(ctx: StateContext<UserStateModel>) {
    const { dispatch } = ctx;
    // console.log('LoadCurrentUserAction triggered');
    const { accessToken } = ctx.getState();
    // console.log('Access token:', accessToken);

    const parsedToken = parseJwt(accessToken, false);
    // console.log('Parsed token:', parsedToken);

    if (!parsedToken || !parsedToken.emails) {
      // Returning here completed the action as a success, so anything chained
      // off dispatch(LoadCurrentUserAction) read a silent no-op as a reloaded
      // user - subscribeToPremiumPlanSuccess toasted "Successfully subscribed"
      // on a user record that was never fetched.
      const error = "Invalid token or missing email in token";
      console.error(error);
      dispatch(new LoadCurrentUserFailAction(error));
      return throwError(() => new Error(error));
    }

    ctx.patchState({ loading: true, error: null });

    // console.log('Sending request to GetCurrentUser with username:', parsedToken.emails);

    const request = this.httpClient.get<User>("GetCurrentUser", {
      params: {
        username: parsedToken.emails,
      },
      observe: "response",
    });

    // currentUserRequestState is unwritten and unread; this handler tracks the
    // request through the flat `loading`/`error` fields instead.
    return dispatchRequest(
      request,
      ctx,
      LoadCurrentUserSuccessAction,
      LoadCurrentUserFailAction
    );
  }

  @Action(LoadCurrentUserSuccessAction)
  loadCurrentUserSuccess(
    { patchState, dispatch }: StateContext<UserStateModel>,
    action: LoadCurrentUserSuccessAction
  ) {
    // console.log('LoadCurrentUserSuccessAction triggered', action.payload);

    patchState({ loading: false, error: null });

    // it means the user not linked to the account
    if (action?.payload?.status === 204) {
      // console.log('User not linked to account, opening modal');
      this.openNoLinkedModalEvent$.next();
      this.storageService.set("isNotLinkedUser", "true");
    } else {
      // console.log('User linked to account, processing user data');
      const { renewalPending, ...userObj } = action.payload.body;
      const currentUser = new User(userObj);
      // console.log('Current user:', currentUser);
      // console.log('Renewal pending:', renewalPending);
      dispatch([
        new SetCurrentUserAction(currentUser),
        new SetCurrentUserPendingVehiclesAction(
          renewalPending?.map((obj) => new Vehicle(obj)) || []
        ),
      ]);
    }
  }

  @Action(LoadCurrentUserFailAction)
  loadCurrentUserFail(
    { patchState }: StateContext<UserStateModel>,
    action: LoadCurrentUserFailAction
  ) {
    console.error("LoadCurrentUserFailAction triggered", action.payload);
    patchState({ loading: false, error: action.payload });
  }

  @Action(GetCurrentUserAction)
  getCurrentUser({ dispatch }: StateContext<UserStateModel>) {
    // console.log('GetCurrentUserAction triggered');
    return this.storageService.get("currentUser").pipe(
      // tap(currentUser => console.log('Retrieved from storage:', currentUser)),
      switchMap((currentUser) => this.handleStoredUser(currentUser, dispatch)),
      catchError((error) => this.handleGetCurrentUserError(error, dispatch))
    );
  }

  private handleStoredUser(currentUser: string | null, dispatch: any) {
    // console.log('handleStoredUser called with:', currentUser);
    if (!currentUser) {
      // console.log('No stored user, dispatching LoadCurrentUserAction');
      return dispatch(new LoadCurrentUserAction());
    }

    return this.parseAndValidateUser(currentUser, dispatch);
  }

  private parseAndValidateUser(currentUser: string, dispatch: any) {
    // console.log('parseAndValidateUser called');
    try {
      const parsedUser = JSON.parse(currentUser);
      // console.log('Parsed user:', parsedUser);
      if (this.isValidUserData(parsedUser)) {
        // console.log('User data is valid, dispatching success actions');
        return this.dispatchSuccessActions(parsedUser, dispatch);
      }
      throw new Error("Invalid user data");
    } catch (error) {
      console.error("Error parsing stored user data:", error);
      return this.clearAndLoadUser(dispatch);
    }
  }

  private isValidUserData(userData: any): boolean {
    // console.log('isValidUserData called with:', userData);
    const isValid = userData && userData.usrUserId && userData.usrEmail;
    // console.log('User data is valid:', isValid);
    return isValid; // Add more checks as needed
  }

  private dispatchSuccessActions(userData: User, dispatch: any) {
    // console.log('dispatchSuccessActions called with:', userData);
    return dispatch([
      new GetCurrentUserSuccessAction({
        body: userData,
        initializedFromStorage: true,
      } as HttpResponse<User> & { initializedFromStorage: boolean }),
      new LoadCurrentUserSuccessAction({
        body: userData,
      } as HttpResponse<User>),
    ]);
  }

  private clearAndLoadUser(dispatch: any) {
    // console.log('clearAndLoadUser called');
    return this.storageService.remove("currentUser").pipe(
      // tap(() => console.log("Cleared invalid user data")),
      switchMap(() => dispatch(new LoadCurrentUserAction())),
      // This runs from getCurrentUser's own catchError, so an unusable token
      // would otherwise escape as an unhandled rejection on the login path.
      // LoadCurrentUserFailAction has already recorded it on the state.
      catchError(() => EMPTY)
    );
  }

  private handleGetCurrentUserError(error: any, dispatch: any) {
    console.error("Error in getCurrentUser:", error);
    return this.clearAndLoadUser(dispatch);
  }

  @Action(GetCurrentUserSuccessAction)
  getCurrentUserSuccess(
    { dispatch }: StateContext<UserStateModel>,
    action: GetCurrentUserSuccessAction
  ) {
    // get the last active feature after logging in
    if (!action?.payload?.initializedFromStorage) {
      dispatch(new GetLastActiveFeatureAction());
    }
  }

  @Action(GetCurrentUserFailAction)
  getCurrentUserFail(
    ctx: StateContext<UserStateModel>,
    action: GetCurrentUserFailAction
  ) {}

  @Action(SetCurrentUserAction)
  setCurrentUser(
    ctx: StateContext<UserStateModel>,
    action: SetCurrentUserAction
  ) {
    const currentUser: User = action.payload;

    ctx.patchState({
      entities: {
        [currentUser.usrUserId]: currentUser,
      },
      ids: [currentUser.usrUserId],
      currentUserId: currentUser.usrUserId,
    });

    this.storageService.set("currentUser", JSON.stringify(currentUser));
  }

  @Action(SetCurrentUserPendingVehiclesAction)
  setCurrentUserPendingVehiclesAction(
    { getState, patchState }: StateContext<UserStateModel>,
    { payload }: SetCurrentUserPendingVehiclesAction
  ) {
    patchState({
      currentUserPendingVehicles: payload,
    });
  }

  @Action(GetAccountInfoAction)
  getAccountInfo(ctx: StateContext<UserStateModel>) {
    const request = this.httpClient.get("GetAccountInfo");
    return dispatchRequest(
      request,
      ctx,
      GetAccountInfoSuccessAction,
      GetAccountInfoFailAction,
      "getAccountInfoRequestState"
    );
  }

  @Action(GetAccountInfoSuccessAction)
  getAccountInfoSuccess(
    ctx: StateContext<UserStateModel>,
    action: GetAccountInfoSuccessAction
  ) {
    const { activeVehicles, ...info } = action.payload;
    const accountInfo = new AccountInfo(info);

    return ctx.dispatch([
      new SetAccountInfoAction(accountInfo),
      new SetActiveVehiclesAction(activeVehicles),
    ]);
  }

  @Action(SaveAccountInfoAction)
  saveAccountInfo(
    ctx: StateContext<UserStateModel>,
    action: SetAccountInfoAction
  ) {
    const { currentUserId } = ctx.getState();
    const request = this.httpClient.put("SaveAccountInfo", action.payload, {
      params: {
        userId: `${currentUserId}`,
      },
    });

    return dispatchRequest(
      request,
      ctx,
      SaveAccountInfoSuccessAction,
      SaveAccountInfoFailAction
    );
  }

  @Action(SetAccountInfoAction)
  setAccountInfo(
    ctx: StateContext<UserStateModel>,
    action: SetAccountInfoAction
  ) {
    return ctx.patchState({
      accountInfo: action.payload,
    });
  }

  @Action(SaveAccountInfoSuccessAction)
  saveAccountInfoSuccess(
    ctx: StateContext<UserStateModel>,
    action: SaveAccountInfoSuccessAction
  ) {
    this.toastService.showToast();
  }

  @Action(LoadMapDataAction)
  loadMapData(ctx: StateContext<UserStateModel>) {
    const currentUser: User =
      ctx.getState().entities[ctx.getState().currentUserId];
    const request = this.httpClient.get("GetMapData", {
      params: {
        customerId: currentUser.usrCustomerId.toString(),
      },
    });

    return dispatchRequest(
      request,
      ctx,
      LoadMapDataSuccessAction,
      null // Assuming there's no specific fail action for this case
    );
  }

  @Action(LoadMapDataSuccessAction)
  loadMapDataSuccess(
    ctx: StateContext<UserStateModel>,
    action: LoadMapDataSuccessAction
  ) {
    const mapData = new MapData(action.payload);

    ctx.patchState({
      mapData,
    });
  }

  @Action(SaveMapDataAction)
  saveMapData(
    ctx: StateContext<UserStateModel>,
    action: SaveMapDataAction
  ) {
    const currentUser: User =
      ctx.getState().entities[ctx.getState().currentUserId];
    const request = this.httpClient.post("PostCustomerMapData", {
      map_CustomerId: currentUser.usrCustomerId,
      map_JSON: action.payload,
    });

    return dispatchRequest(
      request,
      ctx,
      SaveMapDataSuccessAction,
      null // Assuming there's no specific fail action for this case
    );
  }

  @Action(SaveMapDataSuccessAction)
  saveMapDataSuccess(
    ctx: StateContext<UserStateModel>,
    action: SaveMapDataSuccessAction
  ) {
    const mapData = new MapData(action.payload);

    ctx.patchState({
      mapData,
    });
  }

  @Action(CheckAccessTokenAction)
  checkAccessToken(
    ctx: StateContext<UserStateModel>,
    action: CheckAccessTokenAction
  ) {
    return this.storageService.get("accessToken").pipe(
      filter((res) => !!res && !ctx.getState().accessToken),
      tap((accessToken) => {
        ctx.dispatch(new SetAccessTokenAction(accessToken));
      })
    );
  }

  @Action(SetAccessTokenAction)
  setAccessToken(
    ctx: StateContext<UserStateModel>,
    action: SetAccessTokenAction
  ) {
    const accessToken: string = action.payload;

    ctx.patchState({
      accessToken,
    });

    this.storageService.set("accessToken", accessToken);
  }

  @Action(ClearAccessTokenAction)
  clearAccessToken(
    ctx: StateContext<UserStateModel>,
    action: ClearAccessTokenAction
  ) {
    ctx.patchState({
      accessToken: null,
    });

    this.storageService.remove("accessToken");
  }

  @Action(SubscribeToPremiumPlanAction)
  subscribeToPremiumPlan(ctx: StateContext<UserStateModel>) {
    const request = this.httpClient.post(
      "PostSubscribeToPremium",
      {},
      { observe: "response" }
    );

    return dispatchRequest(
      request,
      ctx,
      SubscribeToPremiumPlanSuccessAction,
      SubscribeToPremiumPlanFailAction,
      "subscribeToPremiumPlanRequestState"
    );
  }

  @Action(SubscribeToPremiumPlanSuccessAction)
  subscribeToPremiumPlanSuccess({ dispatch }: StateContext<UserStateModel>) {
    // returned, not fired and forgotten: the request settles only once the
    // reloaded user is in the store, which is what the premium modal waits on
    return dispatch(new LoadCurrentUserAction()).pipe(
      tap(() =>
        this.toastService.showToast("Successfully subscribed as Premium")
      )
    );
  }

  @Action(RenewUserVehiclesSubscriptionAction)
  renewUserVehiclesSubscriptionAction(
    ctx: StateContext<UserStateModel>,
    { payload }: RenewUserVehiclesSubscriptionAction
  ) {
    const request = this.httpClient
      .put("RenewSubscription", payload)
      .pipe(map((res: Vehicle[]) => res?.map((obj) => new Vehicle(obj)) || []));

    return dispatchRequest(
      request,
      ctx,
      RenewUserVehiclesSubscriptionSuccessAction,
      RenewUserVehiclesSubscriptionFailAction,
      "renewUserVehiclesSubscriptionPutRequestState"
    );
  }

  @Action(RenewUserVehiclesSubscriptionSuccessAction)
  renewUserVehiclesSubscriptionSuccessAction(
    { dispatch, getState }: StateContext<UserStateModel>,
    { payload }: RenewUserVehiclesSubscriptionSuccessAction
  ) {
    this.toastService.showToast("Payment Successful");

    const { currentUserPendingVehicles } = getState();
    return dispatch(
      new SetCurrentUserPendingVehiclesAction(
        currentUserPendingVehicles.filter(
          (veh) =>
            !payload.some(
              (vehicle) => veh.vehVehicleId === vehicle.vehVehicleId
            )
        )
      )
    );
  }

  @Action(RenewUserVehiclesSubscriptionFailAction)
  renewUserVehiclesSubscriptionFailAction() {
    return this.toastService.showErrorToast("Payment Unsuccessful");
  }

  @Action(LogoutAction)
  logout(ctx: StateContext<UserStateModel>, action: LogoutAction) {
    const currentUserId: number = ctx.getState().currentUserId;

    const entities = { ...ctx.getState().entities };
    const ids = ctx.getState().ids;

    if (currentUserId) {
      delete entities[currentUserId.toString()];
    }

    ctx.patchState({
      accessToken: null,
      currentUserId: null,
      ids: ids.filter((id) => id !== currentUserId),
      entities,
    });

    this.storageService.remove("isNotLinkedUser");
    this.storageService.remove("accessToken");
    this.storageService.remove("currentUser");
  }
}
